import React, { useState, useEffect } from 'react';
import { Card, Badge, Button, Tag, Alert, Spin } from 'antd';
import { 
  DownOutlined, 
  UpOutlined, 
  WarningOutlined, 
  ExclamationCircleOutlined,
  InfoCircleOutlined 
} from '@ant-design/icons';
import axios from 'axios';
import { BASE_URL } from '../api';

const RiskAnalysisBar = () => {
  const [riskData, setRiskData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const fetchRiskAnalysis = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/api/order-analysis-gpt/last-analysis`, { 
        withCredentials: true 
      });
      if (response.data && response.data.analysis) {
        setRiskData(response.data.analysis);
      }
    } catch (error) {
      console.error('Error fetching risk analysis:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRiskAnalysis();
    // Refresh every 5 seconds like positions
    const interval = setInterval(fetchRiskAnalysis, 5000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <Card style={{ marginBottom: 16, background: '#f5f5f5' }}>
        <Spin /> Loading risk analysis...
      </Card>
    );
  }

  if (!riskData) {
    return (
      <Card style={{ marginBottom: 16, background: '#f5f5f5' }}>
        <Alert message="No risk analysis data available" type="info" />
      </Card>
    );
  }

  const { summary, issues } = riskData;
  
  // Determine risk level based on dangers
  const getRiskLevel = (dangers) => {
    if (dangers >= 3) return { level: 'Critical', color: '#ff4d4f' };
    if (dangers >= 1) return { level: 'High', color: '#ff7a00' };
    return { level: 'Low', color: '#52c41a' };
  };

  const risk = getRiskLevel(summary.dangers);

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'DANGER': return '#ff4d4f';
      case 'WARNING': return '#faad14';
      case 'NOTICE': return '#1890ff';
      default: return '#d9d9d9';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'DANGER': return <ExclamationCircleOutlined />;
      case 'WARNING': return <WarningOutlined />;
      case 'NOTICE': return <InfoCircleOutlined />;
      default: return <InfoCircleOutlined />;
    }
  };

  return (
    <div style={{ 
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      border: '1px solid #f0f0f0',
      marginBottom: '24px',
      overflow: 'hidden'
    }}>
      {/* Header Section */}
      <div style={{
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        padding: '20px 24px',
        borderBottom: '1px solid #e2e8f0'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {/* Left Side - Title */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '32px',
              height: '32px',
              backgroundColor: '#f1f5f9',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px'
            }}>
              🔍
            </div>
            <div>
              <div style={{ 
                fontSize: '20px', 
                fontWeight: '700', 
                color: '#1e293b',
                marginBottom: '2px'
              }}>
                Anomaly Detection
              </div>
              <div style={{ 
                fontSize: '14px', 
                color: '#64748b',
                fontWeight: '400'
              }}>
                {expanded 
                  ? "System suggested position analysis and risk monitoring"
                  : "Click to expand system suggested position analysis"
                }
              </div>
            </div>
          </div>
          
          {/* Right Side - Stats and Controls */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {/* Quick Stats when collapsed */}
            {!expanded && summary && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '14px' }}>
                <span style={{ color: '#64748b', fontWeight: '500' }}>
                  {summary.positions} positions
                </span>
                <span style={{ color: '#e2e8f0' }}>•</span>
                <span style={{ 
                  color: summary.dangers > 0 ? '#ef4444' : '#22c55e', 
                  fontWeight: '600'
                }}>
                  {summary.dangers + summary.warnings} issues
                </span>
                {summary.notices > 0 && (
                  <>
                    <span style={{ color: '#e2e8f0' }}>•</span>
                    <span style={{ color: '#3b82f6', fontWeight: '500' }}>
                      {summary.notices} suggestions
                    </span>
                  </>
                )}
              </div>
            )}
            
            {/* Risk Badge */}
            <div style={{ 
              background: risk.color,
              color: 'white',
              padding: '6px 16px',
              borderRadius: '20px',
              fontSize: '13px',
              fontWeight: '700',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Risk: {risk.level}
            </div>
            
            {/* Expand/Collapse Button */}
            <button
              onClick={() => setExpanded(!expanded)}
              style={{
                background: 'rgba(99, 102, 241, 0.1)',
                border: '1px solid rgba(99, 102, 241, 0.2)',
                borderRadius: '8px',
                padding: '8px 12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: '#6366f1',
                fontSize: '13px',
                fontWeight: '600',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(99, 102, 241, 0.15)';
                e.target.style.borderColor = 'rgba(99, 102, 241, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(99, 102, 241, 0.1)';
                e.target.style.borderColor = 'rgba(99, 102, 241, 0.2)';
              }}
            >
              {expanded ? 'Collapse' : 'Expand'}
              {expanded ? <UpOutlined style={{ fontSize: '10px' }} /> : <DownOutlined style={{ fontSize: '10px' }} />}
            </button>
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div style={{ padding: '24px' }}>
          {/* Stats Grid */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(4, 1fr)', 
            gap: '16px', 
            marginBottom: '24px' 
          }}>
            <div style={{
              background: '#f8fafc',
              borderRadius: '10px',
              padding: '20px',
              textAlign: 'center',
              border: '1px solid #e2e8f0'
            }}>
              <div style={{ 
                fontSize: '28px', 
                fontWeight: '800', 
                color: '#1e293b',
                marginBottom: '4px'
              }}>
                {summary.positions}
              </div>
              <div style={{ 
                fontSize: '13px', 
                color: '#64748b',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Positions
              </div>
            </div>
            
            <div style={{
              background: summary.dangers > 0 ? '#fef2f2' : '#f0fdf4',
              borderRadius: '10px',
              padding: '20px',
              textAlign: 'center',
              border: `1px solid ${summary.dangers > 0 ? '#fecaca' : '#bbf7d0'}`
            }}>
              <div style={{ 
                fontSize: '28px', 
                fontWeight: '800', 
                color: summary.dangers > 0 ? '#ef4444' : '#22c55e',
                marginBottom: '4px'
              }}>
                {summary.dangers}
              </div>
              <div style={{ 
                fontSize: '13px', 
                color: '#64748b',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Dangers
              </div>
            </div>
            
            <div style={{
              background: summary.warnings > 0 ? '#fffbeb' : '#f8fafc',
              borderRadius: '10px',
              padding: '20px',
              textAlign: 'center',
              border: `1px solid ${summary.warnings > 0 ? '#fed7aa' : '#e2e8f0'}`
            }}>
              <div style={{ 
                fontSize: '28px', 
                fontWeight: '800', 
                color: summary.warnings > 0 ? '#f59e0b' : '#64748b',
                marginBottom: '4px'
              }}>
                {summary.warnings}
              </div>
              <div style={{ 
                fontSize: '13px', 
                color: '#64748b',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Warnings
              </div>
            </div>
            
            <div style={{
              background: summary.notices > 0 ? '#eff6ff' : '#f8fafc',
              borderRadius: '10px',
              padding: '20px',
              textAlign: 'center',
              border: `1px solid ${summary.notices > 0 ? '#bfdbfe' : '#e2e8f0'}`
            }}>
              <div style={{ 
                fontSize: '28px', 
                fontWeight: '800', 
                color: summary.notices > 0 ? '#3b82f6' : '#64748b',
                marginBottom: '4px'
              }}>
                {summary.notices}
              </div>
              <div style={{ 
                fontSize: '13px', 
                color: '#64748b',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Notices
              </div>
            </div>
          </div>

          {/* Issues Section */}
          {issues && issues.length > 0 && (
            <div>
              {/* Group issues by severity */}
              {['DANGER', 'WARNING', 'NOTICE'].map(severity => {
                const severityIssues = issues.filter(issue => issue.severity === severity);
                if (severityIssues.length === 0) return null;
                
                const getSeverityConfig = (sev) => {
                  switch (sev) {
                    case 'DANGER': return {
                      title: '🚨 Critical Issues',
                      color: '#ef4444',
                      bgColor: '#fef2f2',
                      borderColor: '#fecaca',
                      icon: '🚨'
                    };
                    case 'WARNING': return {
                      title: '⚠️ Warnings',
                      color: '#f59e0b',
                      bgColor: '#fffbeb',
                      borderColor: '#fed7aa',
                      icon: '⚠️'
                    };
                    case 'NOTICE': return {
                      title: '💡 Suggestions',
                      color: '#3b82f6',
                      bgColor: '#eff6ff',
                      borderColor: '#bfdbfe',
                      icon: '💡'
                    };
                    default: return {
                      title: 'Issues',
                      color: '#6b7280',
                      bgColor: '#f9fafb',
                      borderColor: '#e5e7eb',
                      icon: 'ℹ️'
                    };
                  }
                };
                
                const config = getSeverityConfig(severity);
                
                return (
                  <div key={severity} style={{ marginBottom: '24px' }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px', 
                      marginBottom: '12px'
                    }}>
                      <span style={{ fontSize: '18px' }}>{config.icon}</span>
                      <span style={{ 
                        fontSize: '18px', 
                        fontWeight: '700', 
                        color: config.color 
                      }}>
                        {config.title} ({severityIssues.length})
                      </span>
                    </div>
                    
                    <div style={{ display: 'grid', gap: '12px' }}>
                      {severityIssues.map((issue, index) => (
                        <div key={index} style={{ 
                          background: config.bgColor,
                          border: `1px solid ${config.borderColor}`,
                          borderRadius: '12px', 
                          padding: '20px',
                          borderLeft: `4px solid ${config.color}`
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ 
                                fontWeight: '700', 
                                color: '#1e293b', 
                                marginBottom: '8px',
                                fontSize: '16px'
                              }}>
                                {issue.message}
                              </div>
                              <div style={{ 
                                fontSize: '13px', 
                                color: '#64748b', 
                                marginBottom: '12px',
                                fontWeight: '500'
                              }}>
                                <span style={{ fontWeight: '600' }}>Position:</span> {issue.coin} | 
                                <span style={{ fontWeight: '600' }}> Order ID:</span> {issue.order_id} | 
                                <span style={{ fontWeight: '600' }}> Code:</span> {issue.code}
                              </div>
                              <div style={{ 
                                background: 'rgba(255,255,255,0.7)', 
                                padding: '12px 16px', 
                                borderRadius: '8px',
                                fontSize: '14px',
                                color: '#1e293b',
                                border: '1px solid rgba(0,0,0,0.05)'
                              }}>
                                <span style={{ fontWeight: '700', color: config.color }}>
                                  💡 Action Required: 
                                </span>
                                <span style={{ marginLeft: '6px' }}>
                                  {issue.suggested_fix}
                                </span>
                              </div>
                            </div>
                            
                            <div style={{
                              background: config.color,
                              color: 'white',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: '700',
                              marginLeft: '16px'
                            }}>
                              {issue.coin}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* No Issues State */}
          {(!issues || issues.length === 0) && (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px 20px',
              background: '#f0fdf4',
              borderRadius: '12px',
              border: '1px solid #bbf7d0'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
              <div style={{ 
                fontSize: '20px', 
                fontWeight: '700', 
                color: '#22c55e', 
                marginBottom: '8px' 
              }}>
                All Clear!
              </div>
              <div style={{ fontSize: '14px', color: '#64748b' }}>
                No critical issues detected in your positions.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RiskAnalysisBar;